// Shared attachment list for every email surface: chips with in-app preview
// (image/PDF modal), download, and per-attachment "→ MH" submit when the account
// has a MakersHub API key. Extracted from AccountDetail's inline implementation.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Image as ImageIcon, File, Loader2, Download, Eye, Send, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { getAccountIntegration } from "@/lib/api";
import type { UnifiedEmail } from "@/lib/email/types";
import type { EmailActions } from "./useEmailActions";
import { fetchAttachmentBlobUrl, useEmailAttachments, type EmailAttachmentMeta } from "./useEmailContent";

function fmtSize(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function attIcon(contentType: string) {
  if (contentType.startsWith("image/")) return <ImageIcon className="h-3.5 w-3.5 shrink-0 text-blue-600" />;
  if (contentType === "application/pdf") return <FileText className="h-3.5 w-3.5 shrink-0 text-red-600" />;
  return <File className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
}

export function EmailAttachmentList({ email, actions }: { email: UnifiedEmail; actions: EmailActions }) {
  const { toast } = useToast();
  const [preview, setPreview] = useState<{ url: string; contentType: string; filename: string } | null>(null);
  const [fetching, setFetching] = useState<Set<string>>(new Set());

  const { data: attachments, isLoading, isError } = useEmailAttachments(email.mailbox, email.id, {
    enabled: email.hasAttachments,
  });

  // Per-attachment → MH needs the account's MakersHub API key (forward-only clients have none).
  const { data: mhIntegration } = useQuery({
    queryKey: ["account-integration", email.accountId, "makershub"],
    queryFn: () => getAccountIntegration(email.accountId, "makershub"),
    enabled: email.hasAttachments && !!email.accountId,
    staleTime: 5 * 60_000,
  });
  const canSubmitToMakersHub = !!mhIntegration?.has_api_key;

  const open = async (att: EmailAttachmentMeta, mode: "preview" | "download") => {
    setFetching((prev) => new Set([...prev, att.id]));
    try {
      const blobUrl = await fetchAttachmentBlobUrl(email.mailbox, email.id, att.id, att.name);
      if (mode === "preview") {
        setPreview({ url: blobUrl, contentType: att.contentType, filename: att.name });
      } else {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = att.name;
        a.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
      }
    } catch (err: any) {
      toast({ title: "Failed to fetch attachment", description: err.message, variant: "destructive" });
    } finally {
      setFetching((prev) => {
        const next = new Set(prev);
        next.delete(att.id);
        return next;
      });
    }
  };

  if (!email.hasAttachments) return null;

  return (
    <div className="mt-2">
      {isLoading ? (
        <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading attachments…
        </div>
      ) : isError ? (
        <p className="py-1 text-xs text-muted-foreground">Could not load attachments.</p>
      ) : !attachments?.length ? null : (
        <div className="flex flex-wrap gap-1.5">
          {attachments.filter((a) => !a.isInline).map((att) => {
            const previewable = att.contentType.startsWith("image/") || att.contentType === "application/pdf";
            const busy = fetching.has(att.id);
            const sent = actions.mhSent.has(att.id);
            const sending = actions.mhSubmitting.has(att.id);
            return (
              <div key={att.id} className="flex items-center gap-1 rounded-md border bg-muted/30 py-1 pl-2 pr-1 text-xs">
                {attIcon(att.contentType)}
                <span className="max-w-[180px] truncate" title={att.name}>{att.name}</span>
                <span className="text-[10px] text-muted-foreground">{fmtSize(att.size)}</span>
                {previewable && (
                  <Button variant="ghost" size="icon" className="h-6 w-6" title="Preview" disabled={busy} onClick={() => open(att, "preview")}>
                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-6 w-6" title="Download" disabled={busy} onClick={() => open(att, "download")}>
                  <Download className="h-3 w-3" />
                </Button>
                {canSubmitToMakersHub && (
                  sent ? (
                    <span className="flex items-center gap-0.5 px-1 text-[10px] text-green-700">
                      <Check className="h-3 w-3" /> Sent to MH
                    </span>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 gap-0.5 px-1.5 text-[10px] text-amber-700"
                      title="Submit this attachment to MakersHub"
                      disabled={sending}
                      onClick={() => actions.submitAttachmentToMakersHub(email, att)}
                    >
                      {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                      MH
                    </Button>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* In-app preview modal (image/PDF) */}
      {preview && (
        <Dialog
          open
          onOpenChange={(o) => {
            if (!o) {
              URL.revokeObjectURL(preview.url);
              setPreview(null);
            }
          }}
        >
          <DialogContent className="flex h-[85vh] w-full max-w-4xl flex-col p-0">
            <DialogHeader className="shrink-0 px-4 pb-2 pt-4">
              <DialogTitle className="truncate text-sm font-medium">{preview.filename}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden px-4 pb-4">
              {preview.contentType.startsWith("image/") ? (
                <img src={preview.url} alt={preview.filename} className="h-full w-full object-contain" />
              ) : preview.contentType === "application/pdf" ? (
                <iframe src={preview.url} title={preview.filename} className="h-full w-full rounded border-0" />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Preview not available for this file type.
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
